// ============================================
// components/NotificationBell.tsx
//
// 顶部导航栏的通知铃铛：
//   - 连推送服务 SSE，实时收通知 + 红点
//   - 下拉列表（最近 8 条）+ 底部"查看全部消息"进入 /notifications
//   - 点通知：forum 类站内跳转；app 类弹引导提示（Web 唤不起 Studio）
//   - 点下拉框外部空白处自动关闭
// ⚠️ 上线绑自定义域名后改 PUSH_BASE。
// ============================================

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { getToken } from '../services/api';

const PUSH_BASE = 'https://push.hipiido.cloud';

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  payload: Record<string, unknown>;
  read: boolean;
  created_at: number;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [list, setList] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const esRef = useRef<EventSource | null>(null);
  const retryRef = useRef<number>(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const fetchList = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const resp = await fetch(`${PUSH_BASE}/notifications/list?token=${encodeURIComponent(token)}`);
      const data = await resp.json();
      if (data && data.ok) {
        setList(data.list || []);
        setUnread(data.unread_count || 0);
      }
    } catch (e) { /* 拉取失败不影响页面 */ }
  }, []);

  const connect = useCallback(() => {
    const token = getToken();
    if (!token) return;
    if (esRef.current) { esRef.current.close(); esRef.current = null; }

    const es = new EventSource(`${PUSH_BASE}/sse/connect?token=${encodeURIComponent(token)}`);
    esRef.current = es;

    es.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === 'connected') { retryRef.current = 0; return; }
        setList((prev) => [
          {
            id: msg.id,
            type: msg.type,
            title: msg.title || '',
            payload: msg.payload || {},
            read: false,
            created_at: msg.created_at || Date.now(),
          },
          ...prev,
        ]);
        setUnread((u) => u + 1);
      } catch (e) { /* 忽略非 JSON */ }
    };

    es.onerror = () => {
      if (es.readyState === EventSource.CLOSED) {
        es.close();
        esRef.current = null;
        const delay = Math.min(30000, 2000 * Math.pow(2, retryRef.current));
        retryRef.current += 1;
        setTimeout(() => connect(), delay);
      }
    };
  }, []);

  useEffect(() => {
    if (!getToken()) return;
    fetchList();
    connect();
    return () => {
      if (esRef.current) { esRef.current.close(); esRef.current = null; }
    };
  }, [fetchList, connect]);

  // 点击下拉框外部关闭（mousedown 比遮罩层可靠）
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const toggleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      const token = getToken();
      if (token) {
        try {
          await fetch(`${PUSH_BASE}/notifications/read`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
          });
        } catch (e) { /* 失败下次再标 */ }
      }
      setUnread(0);
      setList((prev) => prev.map((n) => ({ ...n, read: true })));
    }
  };

  // 点通知 → 进该条消息详情页（带整条数据过去）
  const onClickItem = (n: NotificationItem) => {
    setOpen(false);
    navigate(`/notifications/${encodeURIComponent(n.id)}`, { state: { notification: n } });
  };

  if (!getToken()) return null;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={toggleOpen}
        className="relative grid place-items-center w-9 h-9 rounded-full hover:bg-ink-100 transition-colors"
        aria-label="通知"
      >
        <Bell className="w-5 h-5 text-ink-600" strokeWidth={1.8} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-red-500 text-white text-[11px] font-bold leading-none">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-card border border-ink-300/40 z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-ink-300/30 text-sm font-bold text-ink-900">
            最新通知
          </div>
          <div className="max-h-80 overflow-y-auto">
            {list.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-ink-400">
                还没有通知
              </div>
            ) : (
              list.slice(0, 8).map((n) => (
                <button
                  key={n.id}
                  onClick={() => onClickItem(n)}
                  className={`w-full text-left px-4 py-3 border-b border-ink-300/20 hover:bg-paper transition-colors ${n.read ? '' : 'bg-gold-50'}`}
                >
                  <div className="text-sm text-ink-800 leading-snug">{n.title}</div>
                  {n.payload && n.payload.content_preview ? (
                    <div className="text-xs text-ink-400 mt-0.5 truncate">
                      {String(n.payload.content_preview)}
                    </div>
                  ) : null}
                  <div className="text-[11px] text-ink-400 mt-1">{formatTime(n.created_at)}</div>
                </button>
              ))
            )}
          </div>
          <button
            onClick={() => { setOpen(false); navigate('/notifications'); }}
            className="w-full px-4 py-3 text-center text-sm text-gold-600 font-medium hover:bg-paper transition-colors border-t border-ink-300/30"
          >
            查看全部消息
          </button>
        </div>
      )}
    </div>
  );
}

function formatTime(ts: number): string {
  if (!ts) return '';
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60000);
  if (min < 1) return '刚刚';
  if (min < 60) return `${min} 分钟前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 小时前`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} 天前`;
  return new Date(ts).toLocaleDateString('zh-CN');
}
